import CatalogEntity from "../Components/CatalogEntity";
import {useEffect, useState} from "react";
import useHardwareCatalog from "../Hooks/UseHardwareCatalog";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, LinearProgress, TextField} from "@mui/material";
import {MultiSelectTextFieldCellEditor, SelectFieldCellEditor, renderColumnValue, renderNonWarpingHeader, findEnumLabelByValue} from "../../../General/SharedMethods";
import {MRT_EditActionButtons} from "material-react-table";
import CommunicationInterfaceEditComponent from "../Components/CommunicationInterfaceEditComponent";
import * as yup from "yup";

const MeterEditor = ({
                         electricalInterfaceTypeOptions,
                         modbusEncodingOptions,
                         byteOrderOptions,
                         adcMeasurementTypeOptions,
                         meterTypeOptions,
                         communicationInterfaceTypeOptions,
                         controlUnitTypeOptions,
                         enums,
                         booleanOptions
                     }) => {
    const {getAll} = useHardwareCatalog();
    const [manufacturerOptions, setManufacturerOptions] = useState([]);
    const [algoParameterOptions, setAlgoParameterOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const ViewMeterDialog = (columns, viewRow, setViewRow) => {
        const components = [];
        for (let i = 0; i < columns.length; i += 4) {
            components.push(
                <Grid container spacing={2} marginBottom={1} padding={1} key={`row-${i}`}>
                    {[...Array(4)].map((_, j) => {
                        const column = columns[i + j];
                        return column && (
                            <Grid item xs={12} sm={6} md={3} key={column.accessorKey}>
                                <TextField
                                    label={column.header}
                                    defaultValue={renderColumnValue(column, viewRow, enums)}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                    variant="standard"
                                    fullWidth
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }

        return (
            <Dialog maxWidth="md" open={Boolean(viewRow)} onClose={() => setViewRow(null)}>
                <DialogTitle variant="h4">View Details</DialogTitle>
                <DialogContent>
                    {components}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewRow(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    };

    const MeterDialog = (table, row, internalEditComponents, validationErrors, setValidationErrors, dialogType) => {
        const electricalInterfaceTypeIndex = internalEditComponents.findIndex(
            component => component.props.cell.column.id === "communicationInterface.electricalInterfaceType"
        );
        const communicationInterfaceFieldIndex = internalEditComponents.findIndex(
            component => component.props.cell.column.id === "communicationInterface"
        );
        internalEditComponents.splice(electricalInterfaceTypeIndex, 1);
        let communicationInterfaceComponent = null;
        if (communicationInterfaceFieldIndex > -1) {
            communicationInterfaceComponent = (
                <CommunicationInterfaceEditComponent
                    modbusEncodingOptions={modbusEncodingOptions}
                    electricalInterfaceTypeOptions={electricalInterfaceTypeOptions}
                    byteOrderOptions={byteOrderOptions}
                    communicationInterface={row.original.communicationInterface}
                    adcMeasurementTypeOptions={adcMeasurementTypeOptions}
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                    communicationInterfaceTypeOptions={communicationInterfaceTypeOptions}
                    row={row}
                />
            );
            internalEditComponents.splice(communicationInterfaceFieldIndex, 1);
        }

        const components = [];
        for (let i = 1; i < internalEditComponents.length; i += 4) {
            components.push(
                <Grid container spacing={2} marginBottom={1} padding={1} key={`row-${i}`}>
                    {[...Array(4)].map((_, j) => {
                        const component = internalEditComponents[i + j];
                        return component && (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={`item-${i}-${j}`}>
                                {component}
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }

        return (
            <Dialog
                fullWidth
                maxWidth="md"
                open={true}
            >
                <DialogTitle variant="h4">{dialogType === "edit" ? "Edit Meter" : "Create a new Meter"}</DialogTitle>
                <DialogContent
                    sx={{
                        overflowY: 'auto',
                    }}
                >
                    {communicationInterfaceComponent && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>{communicationInterfaceComponent}</Grid>
                    )}
                    {components}
                </DialogContent>
                <DialogActions>
                    <MRT_EditActionButtons variant="text" table={table} row={row}/>
                </DialogActions>
            </Dialog>
        );
    };

    const loadValveTypeAndManufacturerOptions = () => {
        const algoParametersPromise = getAll("algo-parameters");
        const manufacturersPromise = getAll("manufacturers");
        return Promise.all([algoParametersPromise, manufacturersPromise]);
    }

    useEffect(() => {
        loadValveTypeAndManufacturerOptions()
            .then(([algoParameters, manufacturers]) => {
                setAlgoParameterOptions(algoParameters.data.content?.map(algoParameter => ({label: algoParameter.name, value: algoParameter})));
                setManufacturerOptions(manufacturers.data.content?.map(manufacturer => ({label: manufacturer.name, value: manufacturer})));
                setIsLoading(false);
            }).catch(error => {
            setIsLoading(false);
            console.error(error);
        })
    }, []);

    const createMeterColumns = (validationErrors, setValidationErrors) => {
        return [
            {
                accessorKey: 'id',
                header: 'Id',
                enableEditing: false,
                muiEditTextFieldProps: {
                    type: 'number',
                    hidden: true,
                },
            },
            {
                accessorKey: 'name',
                header: 'Name',
                muiEditTextFieldProps: {
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.name,
                    helperText: validationErrors?.name,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            name: undefined,
                        }),
                },
            },
            {
                accessorKey: 'legacyName',
                header: 'Legacy Name',
                muiEditTextFieldProps: {
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.legacyName,
                    helperText: validationErrors?.legacyName,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            legacyName: undefined,
                        }),
                },
            },
            {
                accessorKey: 'legacyId',
                header: 'Legacy ID',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.legacyId,
                    helperText: validationErrors?.legacyId,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            legacyId: undefined,
                        }),
                },
            },
            {
                accessorKey: 'manufacturer',
                header: 'Manufacturer',
                filterVariant: 'multi-select',
                filterSelectOptions: manufacturerOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                Cell: ({cell}) => cell.getValue().name,
                Edit: ({row, column, cell}) => SelectFieldCellEditor(
                    row,
                    column,
                    cell.getValue().name,
                    manufacturerOptions,
                    true,
                    setValidationErrors,
                    validationErrors)
            },
            {
                accessorKey: 'model',
                header: 'Model',
                muiEditTextFieldProps: {
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.model,
                    helperText: validationErrors?.model,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            model: undefined,
                        }),
                },
            },
            {
                accessorKey: 'communicationInterface',
                header: 'Communications Interface',
                filterVariant: 'multi-select',
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                filterSelectOptions: communicationInterfaceTypeOptions.map(option => option.label),
                Header: renderNonWarpingHeader('Communications Interface'),
                Cell: ({row}) => findEnumLabelByValue(row.original.communicationInterface.type, enums)
            },
            {
                accessorKey: 'communicationInterface.electricalInterfaceType',
                header: 'Electrical Interface Type',
                filterVariant: 'multi-select',
                Edit: () => null,
                filterSelectOptions: electricalInterfaceTypeOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                Header: renderNonWarpingHeader('Electrical Interface Type'),
                Cell: ({cell}) => findEnumLabelByValue(cell.getValue(), enums)
            },
            {
                accessorKey: 'meterType',
                header: 'Meter Type',
                editVariant: 'select',
                filterVariant: 'multi-select',
                Cell: ({cell}) => findEnumLabelByValue(cell.getValue(), enums),
                filterSelectOptions: meterTypeOptions.map(option => option.label),
                editSelectOptions: meterTypeOptions,
                muiEditTextFieldProps: {
                    type: 'multi-select',
                    required: true,
                    error: !!validationErrors?.meterType,
                    helperText: validationErrors?.meterType,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            meterType: '',
                        }),
                },
            },
            {
                accessorKey: 'lpHrForOnePulsePerSec',
                header: renderNonWarpingHeader('Lp Hr For One Pulse Per Sec'),
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.lpHrForOnePulsePerSec,
                    helperText: validationErrors?.lpHrForOnePulsePerSec,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            lpHrForOnePulsePerSec: undefined,
                        }),
                },
            },
            {
                accessorKey: 'power',
                header: 'Power',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.power,
                    helperText: validationErrors?.power,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            power: undefined,
                        }),
                },
            },
            {
                accessorKey: 'pulseRatio',
                header: 'Pulse Ratio',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.pulseRatio,
                    helperText: validationErrors?.pulseRatio,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            pulseRatio: undefined,
                        }),
                },
            },
            {
                accessorKey: 'innerDiameter',
                header: 'Inner Diameter',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.innerDiameter,
                    helperText: validationErrors?.innerDiameter,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            innerDiameter: undefined,
                        }),
                },
            },
            {
                accessorKey: 'outerDiameter',
                header: 'Outer Diameter',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.outerDiameter,
                    helperText: validationErrors?.outerDiameter,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            outerDiameter: undefined,
                        }),
                },
            },
            {
                accessorKey: 'minFlowRate',
                header: 'Min Flowrate',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.minFlowRate,
                    helperText: validationErrors?.minFlowRate,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            minFlowRate: undefined,
                        }),
                },
            },
            {
                accessorKey: 'maxFlowRate',
                header: 'Max Flowrate',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.maxFlowRate,
                    helperText: validationErrors?.maxFlowRate,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            maxFlowRate: undefined,
                        }),
                },
            },
            {
                accessorKey: 'manufacturerMaxFlowRate',
                header: 'Manufacturer Max Flowrate',
                Header: renderNonWarpingHeader('Manufacturer Max Flowrate'),
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.manufacturerMaxFlowRate,
                    helperText: validationErrors?.manufacturerMaxFlowRate,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            manufacturerMaxFlowRate: undefined,
                        }),
                },
            },
            {
                accessorKey: 'startFlowRate',
                header: 'Start Flowrate',
                muiEditTextFieldProps: {
                    type: 'number',
                    error: !!validationErrors?.startFlowRate,
                    helperText: validationErrors?.startFlowRate,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            startFlowRate: undefined,
                        }),
                },
            },
            {
                accessorKey: 'maxPressure',
                header: 'Max Pressure',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.maxPressure,
                    helperText: validationErrors?.maxPressure,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            maxPressure: undefined,
                        }),
                },
            },
            {
                accessorKey: 'poles',
                header: 'Poles',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: false,
                    error: !!validationErrors?.poles,
                    helperText: validationErrors?.poles,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            poles: undefined,
                        }),
                },
            },
            {
                accessorKey: 'maxTemperature',
                header: 'Max Temperature',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.maxTemperature,
                    helperText: validationErrors?.maxTemperature,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            maxTemperature: undefined,
                        }),
                },
            },
            {
                accessorKey: 'ufr',
                header: 'UFR',
                editVariant: 'select',
                filterVariant: 'select',
                filterSelectOptions: booleanOptions,
                editSelectOptions: booleanOptions,
                Cell: ({cell}) => findEnumLabelByValue(cell.getValue(), enums),
                muiEditTextFieldProps: {
                    type: 'select',
                    required: false,
                    error: !!validationErrors?.ufr,
                    helperText: validationErrors?.ufr,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            ufr: '',
                        }),
                },
            },
            {
                accessorKey: 'highSensitivity',
                header: 'High Sensitivity',
                editVariant: 'select',
                filterVariant: 'select',
                filterSelectOptions: booleanOptions,
                editSelectOptions: booleanOptions,
                Cell: ({cell}) => findEnumLabelByValue(cell.getValue(), enums),
                muiEditTextFieldProps: {
                    type: 'select',
                    required: true,
                    error: !!validationErrors?.highSensitivity,
                    helperText: validationErrors?.highSensitivity,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            highSensitivity: '',
                        }),
                },
            },
            {
                accessorKey: 'algoParameters',
                header: 'Algo Parameters',
                filterVariant: 'multi-select',
                filterSelectOptions: algoParameterOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                Cell: ({cell}) => cell.getValue().name,
                Edit: ({row, column, cell}) => SelectFieldCellEditor(
                    row,
                    column,
                    cell.getValue().name,
                    algoParameterOptions,
                    true,
                    setValidationErrors,
                    validationErrors)
            },
            {
                accessorKey: 'compatibleControlUnitTypes',
                header: 'Compatible Control Unit Types',
                Header: renderNonWarpingHeader('Compatible Control Unit Types'),
                filterVariant: 'multi-select',
                filterSelectOptions: controlUnitTypeOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                Cell: ({cell}) => cell.getValue().map((controlUnit, index, array) => findEnumLabelByValue(controlUnit, enums) + (index < array.length - 1 ? ', ' : '')),
                Edit: ({row, column, cell}) => MultiSelectTextFieldCellEditor(
                    row,
                    column,
                    cell.getValue(),
                    controlUnitTypeOptions,
                    true,
                    setValidationErrors,
                    validationErrors)
            },
        ]
    }

    const meterValidationSchema = yup.object().shape({
        name: yup.string().required('Name is required').max(255),
        legacyName: yup.string().required('Legacy Name is required').max(255),
        legacyId: yup.number().typeError('legacy ID is required').required('legacy ID is required'),
        manufacturer: yup.object().typeError('Manufacturer is required').required('Manufacturer is required'),
        model: yup.string().required('Model is required').max(255),
        meterType: yup.string().required('Meter Type is required'),
        communicationInterface: yup.object().typeError('Communication Interface Type is required').shape({
            electricalInterfaceType: yup.string().required('Electrical Interface Type is required'),
            adcMeasurementType: yup.string().when('type', {
                is: 'adc',
                then: yup.string().required('ADC Measurement Type is required'),
                otherwise: yup.string().notRequired(),
            }),
            minValue: yup.number().when('type', {
                is: 'adc',
                then: yup.number().required('Min Value is required'),
                otherwise: yup.number().notRequired(),
            }),
            maxValue: yup.number().when('type', {
                is: 'adc',
                then: yup.number().required('Max Value is required'),
                otherwise: yup.number().notRequired(),
            }),
        }),
        lpHrForOnePulsePerSec: yup.number().typeError('Lp Hr For One Pulse Per Sec is required').required('Lp Hr For One Pulse Per Sec is required').min(36).max(1800000),
        pulseRatio: yup.number().typeError('Pulse Ratio is required').required('Pulse Ratio is required').min(0.01).max(500),
        innerDiameter: yup.number().typeError('Inner Diameter is required').required('Inner Diameter is required').min(0.05).max(12),
        outerDiameter: yup.number().typeError('Outer Diameter is required').required('Outer Diameter is required').min(0.05).max(12).test('is-greater', 'Outer Diameter must be greater than or equal to Inner Diameter', function (value) {
            const {innerDiameter} = this.parent;
            return value >= innerDiameter;
        }),
        minFlowRate: yup.number().typeError('Min Flow Rate is required').required('Min Flow Rate is required').min(1).max(10000),
        maxFlowRate: yup.number().typeError('Max Flow Rate is required').required('Max Flow Rate is required').min(1000).max(1500000),
        manufacturerMaxFlowRate: yup.number().typeError('Manufacturer Max Flow Rate is required').required('Manufacturer Max Flow Rate is required').min(1000).max(1500000),
        maxPressure: yup.number().typeError('Max Pressure is required').required('Max Pressure is required'),
        poles: yup.number()
            .nullable(true)
            .transform((value, originalValue) => (originalValue === '' ? undefined : value))
            .when('meterType', (meterType, schema) => {
                return meterType === 'impeller'
                    ? schema.required('Poles are required for Impeller Meter Type').typeError('Poles must be a number')
                    : schema.notRequired();
            }),
        maxTemperature: yup.number().typeError('Max Temperature is required').required('Max Temperature is required'),
        highSensitivity: yup.boolean().typeError('High Sensitivity is required').required('High Sensitivity is required'),
        algoParameters: yup.object().typeError('Algo Parameters are required').required('Algo Parameters are required'),
        compatibleControlUnitTypes: yup.array().of(yup.string()).typeError('Compatible Control Unit Types are required').required('Compatible Control Unit Types are required'),
        power: yup.number().typeError('Power is required').required('Power is required').min(0).max(300)
    });

    return isLoading ? <LinearProgress/> : <CatalogEntity entityType={'meters'}
                                                          entityName={'Meter'}
                                                          createColumns={createMeterColumns}
                                                          validationSchema={meterValidationSchema}
                                                          customEditDialog={MeterDialog}
                                                          customCreateDialog={MeterDialog}
                                                          customViewDialog={ViewMeterDialog}
                                                          enums={enums}

    />

}


export default MeterEditor;
